import React, { memo, Suspense, useLayoutEffect, useMemo, type ReactNode } from 'react';
import { matchPath, Route, Routes, useLocation } from 'react-router';
import { privateRoutes, publicRoutes } from '@/router';
import Preloader from '@srm/ui/preloader';
import ReportSkeleton from '@pages/reports/ReportSkeleton/ReportSkeleton';
import GlobalSearchSkeleton from '@pages/globalSearch/GlobalSearchSkeleton/GlobalSearchSkeleton';
import NotificationSkeleton from '@pages/notification/NotificationSkeleton/NotificationSkeleton';
import { parseSearchRoute } from '@pages/globalSearch/searchRoute';
import SearchFolderSkeleton from '@organisms/documents/SearchFolderSkeleton';
import SectionHubSkeleton from '@organisms/SectionHub/SectionHubSkeleton';
import DocumentSkeleton from '@organisms/document/DocumentSkeleton';
import DashboardSkeleton from '@organisms/dashboard/DashboardSkeleton/DashboardSkeleton';
import ProtectedRoute from '@/components/auth/ProtectedRoute';
import { useTypedSelector } from '@hooks/useTypedSelector';
import { RouteNames } from '@/router/router.types';

const NotFoundPage = React.lazy(() => import('@pages/notFound/NotFoundPage'));

const DOCUMENT_ROUTES = [
    RouteNames.DOCUMENT,
    RouteNames.DOCUMENT_CREATE_ON_BASE,
    RouteNames.DOCUMENT_CREATE_ON_BASE_TEMPLATE,
    RouteNames.DOCUMENT_CREATE_BY_ACTION,
    RouteNames.DOCUMENT_CREATE_BY_ACTION_TEMPLATE,
];

interface IAppRouterProps {
    onReady?: () => void;
    hideLoadingFallback?: boolean;
}

const RouteReady = ({ children, onReady }: { children: ReactNode; onReady?: () => void }) => {
    useLayoutEffect(() => {
        onReady?.();
    }, [onReady]);
    return children;
};

// Каркас загрузки выбирается по адресу сам: AppRouter адрес не читает, иначе каждая смена адреса
// (включая query) пересоздавала бы элементы маршрутов и перерисовывала текущую страницу целиком.
const RouteLoadingFallback = () => {
    const { pathname, search } = useLocation();
    const params = new URLSearchParams(search);
    // Каркас соответствует странице и до загрузки её кода, и во время запросов.
    const isDocumentRoute = DOCUMENT_ROUTES.some((path) => matchPath(path, pathname) !== null);
    const isDashboardRoute = matchPath(RouteNames.DASHBOARD, pathname) !== null;
    const isListSection = [RouteNames.TASKS, RouteNames.DOCUMENTS].some((path) => matchPath(path, pathname));
    const isDictionarySection = matchPath(RouteNames.DICTIONARIES, pathname) !== null;
    const isReportSection = matchPath(RouteNames.REPORTS, pathname) !== null;
    const isGlobalSearch = matchPath(RouteNames.SEARCH, pathname) !== null;
    const isNotification = matchPath(RouteNames.NOTIFICATION, pathname) !== null;
    const searchRoute = isGlobalSearch ? parseSearchRoute(params, undefined) : undefined;
    const isSearchFolder =
        (isListSection && !!params.get('searchType')) ||
        (isDictionarySection && !!params.get('dictName')) ||
        (isReportSection && !!params.get('searchFolder'));
    const isSectionHub = isListSection || isDictionarySection || (isReportSection && !params.get('form'));
    return isDocumentRoute ? (
        <DocumentSkeleton />
    ) : isDashboardRoute ? (
        <DashboardSkeleton />
    ) : isSearchFolder ? (
        <SearchFolderSkeleton />
    ) : isSectionHub ? (
        <SectionHubSkeleton />
    ) : isReportSection ? (
        <ReportSkeleton name={params.get('name') ?? ''} />
    ) : searchRoute ? (
        <GlobalSearchSkeleton text={searchRoute.text} mode={searchRoute.mode} />
    ) : isNotification ? (
        <NotificationSkeleton />
    ) : (
        <Preloader size="xl" label="Загрузка" />
    );
};

// Набор маршрутов задан при загрузке модуля, поэтому элементы зависят только от onReady: `<Routes>`
// подписан на адрес сам и на переходе получает те же элементы — страница, не читающая адрес, пропускается.
const buildRoutes = (onReady?: () => void) => [
    ...publicRoutes.map((route) => (
        <Route
            path={route.path}
            element={
                <RouteReady onReady={onReady}>
                    <route.component />
                </RouteReady>
            }
            key={route.path}
        />
    )),
    ...privateRoutes.map((route) => (
        <Route
            path={route.path}
            element={
                <ProtectedRoute
                    element={
                        <RouteReady onReady={onReady}>
                            <route.component />
                        </RouteReady>
                    }
                    allowAccessTokenOnly={route.accessTokenOnly}
                />
            }
            key={route.path}
        />
    )),
    <Route
        path="*"
        element={
            <RouteReady onReady={onReady}>
                <NotFoundPage />
            </RouteReady>
        }
        key="*"
    />,
];

const AppRouter = ({ onReady, hideLoadingFallback = false }: IAppRouterProps = {}) => {
    const isLoading = useTypedSelector((state) => state.auth.isLoading);
    const routes = useMemo(() => buildRoutes(onReady), [onReady]);
    const loadingFallback = hideLoadingFallback ? null : <RouteLoadingFallback />;

    if (isLoading) return loadingFallback;

    return (
        <Suspense fallback={loadingFallback}>
            <Routes>{routes}</Routes>
        </Suspense>
    );
};

export default memo(AppRouter);
